import type {
  ReportVerificationInfo,
  Scan,
  ScanListItem,
  StartScanResponse,
} from "../api";
import {
  CHECKPOINTS,
  DEMO_IDS,
  DEMO_PIPELINE,
  demoEvents,
  demoFindings,
  demoGraphEdges,
  demoGraphNodes,
  demoNotifications,
  demoSubscription,
  DEMO_VECTORS,
  findingFixture,
  vectorStatusAt,
} from "./demoData";
import {
  addTarget,
  cancelScan,
  completeVerify,
  createProject,
  markNotificationRead,
  pauseScan,
  resumeScan,
  startScan,
  startVerify,
} from "./demoScript";
import { demoStore, type DemoStore } from "./demoStore";
import type { Checkpoint, DemoScan, DemoState } from "./demoTypes";

/** API path prefixes the demo answers itself. Everything else (auth, billing
 *  checkout, invoices, jurisdiction catalog) goes to the real server. */
const SCOPES = [
  "/api/v1/projects",
  "/api/v1/scope",
  "/api/v1/scans",
  "/api/v1/notifications",
];

/** GET /billing/subscription is answered by the demo; the other billing routes
 *  (checkout, plans, invoices) stay real. */
export function inDemoScope(method: string, path: string): boolean {
  const p = path.split("?")[0];
  if (method.toUpperCase() === "GET" && p === "/api/v1/billing/subscription") return true;
  return SCOPES.some((s) => p === s || p.startsWith(`${s}/`));
}

export interface DemoResponse {
  status: number;
  data: unknown;
}

const ok = (data: unknown): DemoResponse => ({ status: 200, data });
const notFound = (): DemoResponse => ({ status: 404, data: { detail: "Not found (demo data)" } });

interface Ctx {
  store: DemoStore;
  state: DemoState;
  params: Record<string, string>;
  query: URLSearchParams;
  body: Record<string, unknown>;
  now: number;
}

type Handler = (ctx: Ctx) => DemoResponse;

interface Route {
  method: string;
  keys: string[];
  pattern: RegExp;
  handler: Handler;
}

function compile(method: string, path: string, handler: Handler): Route {
  const keys: string[] = [];
  const source = path
    .split("/")
    .map((seg) => {
      if (seg.startsWith(":")) {
        keys.push(seg.slice(1));
        return "([^/]+)";
      }
      return seg.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    })
    .join("/");
  return { method: method.toLowerCase(), keys, pattern: new RegExp(`^${source}$`), handler };
}

// ---- Scan view helpers --------------------------------------------------------

function startedAtMsOf(scan: DemoScan): number {
  return new Date(scan.started_at).getTime();
}

function scanInfo(state: DemoState): Scan | null {
  const scan = state.scan;
  if (!scan) return null;
  const cp = CHECKPOINTS[scan.checkpoint];
  const findings = demoFindings(state.host ?? "", scan.scan_id, scan.checkpoint, startedAtMsOf(scan));
  const critical = findings.filter((f) => f.severity === "critical").length;
  const high = findings.filter((f) => f.severity === "high").length;
  return {
    scan_id: scan.scan_id,
    project_id: DEMO_IDS.project,
    status: scan.status,
    pipeline_name: DEMO_PIPELINE.name,
    pipeline_version: DEMO_PIPELINE.version,
    current_node_id: cp.currentNode,
    progress_percent: cp.progress,
    nodes_total: 18,
    nodes_done: Math.round((cp.progress / 100) * 18),
    findings_count: findings.length,
    critical_count: critical,
    high_count: high,
    created_at: scan.started_at,
    started_at: scan.started_at,
    completed_at: scan.completed_at,
  };
}

function scanListItem(state: DemoState): ScanListItem | null {
  const info = scanInfo(state);
  if (!info) return null;
  return {
    scan_id: info.scan_id,
    target_id: DEMO_IDS.target,
    project_id: info.project_id,
    status: info.status,
    pipeline_name: info.pipeline_name,
    pipeline_version: info.pipeline_version,
    current_node_id: info.current_node_id,
    progress_percent: info.progress_percent,
    nodes_total: info.nodes_total,
    nodes_done: info.nodes_done,
    findings_count: info.findings_count,
    critical_count: info.critical_count,
    high_count: info.high_count,
    created_at: info.created_at,
    started_at: info.started_at,
    completed_at: info.completed_at,
  };
}

/** Attack vectors emitted as events for deriveAttackVectorsFromEvents — but the
 *  demo can answer /findings etc. directly, so we build the vectors view too
 *  for any caller that asks (listAttackVectors pages /events). Not a route
 *  itself; the events route carries the hypothesis events. */
export function demoVectorsCount(k: Checkpoint): number {
  if (k < 2) return 0;
  return DEMO_VECTORS.filter((v) => vectorStatusAt(v, k) !== null).length;
}

function reportVerification(state: DemoState): ReportVerificationInfo {
  const completed = state.scan?.status === "completed";
  const total = demoFindings(state.host ?? "", DEMO_IDS.scan, 5, 0).length;
  return {
    scan_id: DEMO_IDS.scan,
    manual_verification_state: completed ? "verified" : "not_completed",
    finding_selection_mode: "automated",
    status: completed ? "verified" : "unverified",
    is_verified: completed,
    verification_id: completed ? "demo-verif" : null,
    review_revision: 1,
    verified_revision: completed ? 1 : null,
    verified_by: completed ? { user_id: DEMO_IDS.pentester, display_name: "A. Volkov" } : null,
    verified_at: completed ? new Date().toISOString() : null,
    snapshot_counts: completed
      ? { total, included: total, excluded_unreviewed: 0, excluded_false_positive: 0 }
      : null,
    current_counts: { total, confirmed: total, false_positive: 0, unreviewed: 0 },
    strict_mode: false,
    can_finalize: completed,
  };
}

// ---- Routes -------------------------------------------------------------------

const ROUTES: Route[] = [
  // ----- projects -----
  compile("get", "/api/v1/projects", ({ state }) =>
    ok({ projects: state.project ? [state.project] : [], count: state.project ? 1 : 0 }),
  ),
  compile("post", "/api/v1/projects", ({ store, state, body, now }) => {
    store.apply(createProject(state, body as { name?: string; description?: string; jurisdiction_code?: string }, now));
    return ok(store.getState().project);
  }),
  compile("get", "/api/v1/projects/:id", ({ state, params }) =>
    state.project && params.id === state.project.project_id ? ok(state.project) : notFound(),
  ),
  compile("patch", "/api/v1/projects/:id", ({ store, state, params, body }) => {
    if (!state.project || params.id !== state.project.project_id) return notFound();
    const code = typeof body.jurisdiction_code === "string" ? body.jurisdiction_code : state.project.jurisdiction_code;
    store.update((s) => (s.project ? { ...s, project: { ...s.project, jurisdiction_code: code } } : s));
    return ok(store.getState().project);
  }),
  compile("delete", "/api/v1/projects/:id", ({ store }) => {
    store.update((s) => ({ ...s, project: null, target: null, scan: null }));
    return ok({ deleted: true });
  }),

  // ----- scope / targets -----
  compile("get", "/api/v1/projects/:id/scope/targets", ({ state }) =>
    ok({ targets: state.target ? [state.target] : [] }),
  ),
  compile("post", "/api/v1/projects/:id/scope/targets", ({ store, state, body, now }) => {
    store.apply(addTarget(state, String(body.target ?? ""), now));
    return ok(store.getState().target);
  }),
  compile("get", "/api/v1/scope/targets/:id", ({ state }) => (state.target ? ok(state.target) : notFound())),
  compile("post", "/api/v1/scope/targets/:id/verification/start", ({ store, state, now }) => {
    store.apply(startVerify(state, now));
    return ok(store.getState().target);
  }),
  compile("post", "/api/v1/scope/targets/:id/verification/complete", ({ store, state, now }) => {
    store.apply(completeVerify(state, now));
    const target = store.getState().target;
    return ok({ target, verified: true, failure_reason: null });
  }),

  // ----- scans -----
  compile("post", "/api/v1/scans", ({ store, state, now }): DemoResponse => {
    store.apply(startScan(state, now));
    const resp: StartScanResponse = {
      scan_id: DEMO_IDS.scan,
      status: "running",
      target_id: DEMO_IDS.target,
      project_id: DEMO_IDS.project,
      pipeline_name: DEMO_PIPELINE.name,
      pipeline_version: DEMO_PIPELINE.version,
    };
    return ok(resp);
  }),
  compile("get", "/api/v1/scans", ({ state }) => {
    const item = scanListItem(state);
    return ok({ scans: item ? [item] : [], count: item ? 1 : 0 });
  }),
  compile("get", "/api/v1/scans/:id", ({ state, params }) => {
    const info = scanInfo(state);
    return info && params.id === info.scan_id ? ok(info) : notFound();
  }),
  compile("post", "/api/v1/scans/:id/pause", ({ store, state, now }) => {
    store.apply(pauseScan(state, now));
    return ok(scanInfo(store.getState()) ?? notFound().data);
  }),
  compile("post", "/api/v1/scans/:id/resume", ({ store, state, now }) => {
    store.apply(resumeScan(state, now));
    return ok(scanInfo(store.getState()) ?? notFound().data);
  }),
  compile("post", "/api/v1/scans/:id/cancel", ({ store, state }) => {
    const info = scanInfo(state);
    store.apply(cancelScan(state));
    return ok(info ? { ...info, status: "cancelled" } : { cancelled: true });
  }),
  compile("get", "/api/v1/scans/:id/findings", ({ state, params }) => {
    if (!state.scan || params.id !== state.scan.scan_id) return notFound();
    const findings = demoFindings(state.host ?? "", state.scan.scan_id, state.scan.checkpoint, startedAtMsOf(state.scan));
    return ok({ scan_id: state.scan.scan_id, findings, total: findings.length });
  }),
  compile("get", "/api/v1/scans/:id/findings/:fid", ({ state, params }) => {
    if (!state.scan) return notFound();
    const fixture = findingFixture(params.fid);
    if (!fixture || fixture.at > state.scan.checkpoint) return notFound();
    const all = demoFindings(state.host ?? "", state.scan.scan_id, state.scan.checkpoint, startedAtMsOf(state.scan));
    const hit = all.find((f) => f.finding_id === params.fid);
    return hit ? ok(hit) : notFound();
  }),
  compile("get", "/api/v1/scans/:id/graph", ({ state, params }) => {
    if (!state.scan || params.id !== state.scan.scan_id) return notFound();
    return ok({
      scan_id: state.scan.scan_id,
      pipeline_name: DEMO_PIPELINE.name,
      pipeline_version: DEMO_PIPELINE.version,
      nodes: demoGraphNodes(state.scan.checkpoint, state.scan.status === "completed"),
      edges: demoGraphEdges(),
    });
  }),
  compile("get", "/api/v1/scans/:id/events", ({ state, params, query }) => {
    if (!state.scan || params.id !== state.scan.scan_id) {
      return ok({ scan_id: params.id, cursor: "", next_cursor: "", events: [], count: 0, has_more: false });
    }
    let events = demoEvents(
      state.host ?? "",
      state.scan.scan_id,
      state.scan.checkpoint,
      startedAtMsOf(state.scan),
      state.scan.revealed,
    );
    const kinds = query.get("kinds");
    if (kinds) {
      const allow = new Set(kinds.split(",").map((k) => k.trim()).filter(Boolean));
      events = events.filter((e) => allow.has(e.event_type));
    }
    // Default view wants newest first (Overview/Feed); chat sorts itself.
    const ordered = [...events].reverse();
    return ok({
      scan_id: state.scan.scan_id,
      cursor: "",
      next_cursor: "",
      events: ordered,
      count: ordered.length,
      has_more: false,
    });
  }),
  compile("get", "/api/v1/scans/:id/report/verification", ({ state }) => ok(reportVerification(state))),

  // ----- notifications -----
  compile("get", "/api/v1/notifications", ({ state }) => {
    if (!state.scan) return ok({ notifications: [], count: 0 });
    const notifs = demoNotifications(
      state.host ?? "",
      state.scan.checkpoint,
      startedAtMsOf(state.scan),
      state.readNotificationIds,
    );
    return ok({ notifications: notifs, count: notifs.length });
  }),
  compile("post", "/api/v1/notifications/:id/read", ({ store, state, params }) => {
    store.apply(markNotificationRead(state, params.id));
    return ok({ updated: true });
  }),

  // ----- billing -----
  compile("get", "/api/v1/billing/subscription", () => ok(demoSubscription())),
];

function matchRoute(method: string, path: string): { route: Route; params: Record<string, string> } | null {
  const p = path.split("?")[0];
  const m = method.toLowerCase();
  for (const route of ROUTES) {
    if (route.method !== m) continue;
    const match = route.pattern.exec(p);
    if (!match) continue;
    const params: Record<string, string> = {};
    route.keys.forEach((k, i) => {
      params[k] = decodeURIComponent(match[i + 1]);
    });
    return { route, params };
  }
  return null;
}

/** Answer one request from the demo state. Unknown paths in scope get an empty
 *  success (never an error) so a stray screen renders its empty state. */
export function handleDemoRequest(
  store: DemoStore,
  method: string,
  path: string,
  body: unknown,
  now = Date.now(),
): DemoResponse {
  const hit = matchRoute(method, path);
  const query = new URLSearchParams(path.split("?")[1] ?? "");
  if (hit) {
    return hit.route.handler({
      store,
      state: store.getState(),
      params: hit.params,
      query,
      body: (body ?? {}) as Record<string, unknown>,
      now,
    });
  }
  if (method.toUpperCase() === "GET") return ok({ items: [], count: 0 });
  return ok({ ok: true });
}

/** Is the url a demo scan report? (The report goes through fetch, not request.) */
export function demoReportMatch(url: string): { scanId: string; format: string } | null {
  if (!demoStore.active) return null;
  const m = /\/api\/v1\/scans\/([^/?]+)\/report(?:\?format=([a-z]+))?/.exec(url);
  if (!m) return null;
  return { scanId: decodeURIComponent(m[1]), format: m[2] ?? "pdf" };
}

export { demoStore };
